// #region imports

// --- Angular
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges } from '@angular/core';

// --- RxJs
import { BehaviorSubject } from 'rxjs';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { Constants, GroupModel, LanguageModel, RespondentEmailValidationModel, RespondentModel, SelectListItem, SurveyResponseModel } from '../../../models';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { GenericValidator, RangeValidator } from '../../../shared';

// #endregion

const nonWhiteSpaceRegExp: RegExp = new RegExp("\\S");

@Component({
  selector: 'hs-add-respondent',
  templateUrl: './add-respondent.component.html',
  styleUrls: ['./add-respondent.component.scss']
})
export class AddRespondentComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() group: GroupModel | null = null;
  @Input() isAddMode: boolean = false;
  @Input() languages: LanguageModel[] | null = null;
  @Input() previousRespondents: RespondentModel[] | null = null;
  @Input() respondentIndex: number = 0;
  @Input() respondent: RespondentModel = {} as RespondentModel;
  @Input() response: SurveyResponseModel | null = null;
  @Input() respondentValidationResult: RespondentEmailValidationModel | null = null;
  @Input() showCancel: boolean = false;
  @Input() validationErrorMessage: string = '';

  private currentEmailAddress: string = '';

  validationMessageFirstNameRequired: string;
  validationMessageFirstNameMaxLength: string;
  validationMessageLastNameRequired: string;
  validationMessageLastNameMaxLength: string;
  validationMessageEmailAddressRequired: string;
  validationMessageEmailAddressMaxLength: string;
  validationMessageEmailAddressInvalid: string;
  validationMessageLanguageRequired: string;
  validationMessagePersonalMessageMaxLength: string;

  @Output() backButtonClicked = new EventEmitter();
  @Output() clearValidation = new EventEmitter();
  @Output() dismissModal = new EventEmitter();
  @Output() emailAddressChanged = new EventEmitter<RespondentEmailValidationModel>();
  @Output() invitePreviousClicked = new EventEmitter();
  @Output() saveRespondent = new EventEmitter<RespondentModel>();

  displayMessage: { [id: string]: string } = {};
  isAddPreviousEnabled: boolean = false;
  isValid: boolean = false;
  respondentForm: FormGroup<any>;

  localLanguages$: BehaviorSubject<SelectListItem[]>;

  private genericValidator: GenericValidator | undefined;
  private minLanguageId = 1;
  private maxLanguageId = 30;
  private validationMessages: { [key: string]: { [key: string]: string } } = {};

  constructor(
    private _formBuilder: FormBuilder,
    private _renderer: Renderer2,
    private _translateService: TranslateService,
  ) {
    this.localLanguages$ = new BehaviorSubject<SelectListItem[]>([ {
      text: this._translateService.instant('commonTextLoading') ,
      value: '',
      isSelected: true
    } as SelectListItem ]);
    this.respondentForm = this._formBuilder.group({
      firstName: new FormControl(
        { value: '', disabled: false},
        [
          Validators.required,
          Validators.maxLength(Constants.FIRST_NAME_MAXIMUM_LENGTH),
          Validators.pattern(nonWhiteSpaceRegExp)
        ]
      ),
      lastName: new FormControl(
        { value: '', disabled: false},
        [
          Validators.required,
          Validators.maxLength(Constants.LAST_NAME_MAXIMUM_LENGTH),
          Validators.pattern(nonWhiteSpaceRegExp)
        ]
      ),
      emailAddress: new FormControl(
        { value: '', disabled: false},
        [
          Validators.required,
          Validators.maxLength(Constants.EMAIL_ADDRESS_MAXIMUM_LENGTH),
          Validators.email,
          Validators.pattern(nonWhiteSpaceRegExp)
        ]
      ),
      language: new FormControl(
        { value: '', disabled: false },
        [
          Validators.required,
          RangeValidator(this.minLanguageId, this.maxLanguageId)
        ]
      ),
      personalMessage: new FormControl(
        { value: '', disabled: false},
        [
          Validators.maxLength(
            Constants.INVITE_RESPONDENTS_PERSONAL_MESSAGE_MAX_LENGTH
          )
        ]
      )
    });

    this.respondent = {} as RespondentModel;

    this.validationMessageFirstNameRequired = this._translateService.instant('firstNameRequired');
    this.validationMessageFirstNameMaxLength = this._translateService.instant('firstNameExceed100');
    this.validationMessageLastNameRequired = this._translateService.instant('lastNameRequired');
    this.validationMessageLastNameMaxLength = this._translateService.instant('lastNameExceed100');
    this.validationMessageEmailAddressRequired = this._translateService.instant('emailAddressRequired');
    this.validationMessageEmailAddressMaxLength =  this._translateService.instant('emailAddressExceed256');
    this.validationMessageEmailAddressInvalid = this._translateService.instant('supplyValidEmailAddress');
    this.validationMessagePersonalMessageMaxLength = this._translateService.instant('personalMessageExceed2000');
    this.validationMessageLanguageRequired = this._translateService.instant('languageRequired');
  }

  ngOnInit(): void {
    this.displayMessage = {};
    this.validationMessages = {
      firstName: {
        required: this.validationMessageFirstNameRequired,
        maxlength: this.validationMessageFirstNameMaxLength,
        pattern: this.validationMessageFirstNameRequired
      },
      lastName: {
        required: this.validationMessageLastNameRequired,
        maxlength: this.validationMessageLastNameMaxLength,
        pattern: this.validationMessageLastNameRequired
      },
      emailAddress: {
        required: this.validationMessageEmailAddressRequired,
        maxlength: this.validationMessageEmailAddressMaxLength,
        email: this.validationMessageEmailAddressInvalid,
        pattern: this.validationMessageEmailAddressRequired
      },
      language: {
        required: this.validationMessageLanguageRequired,
        rangeValidator: this.validationMessageLanguageRequired
      },
      personalMessage: {
        maxlength: this.validationMessagePersonalMessageMaxLength
      }
    };

    this.genericValidator = new GenericValidator(this.validationMessages);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['languages'] && changes['languages'].currentValue) {
      const localLanguages = new Array<SelectListItem>();
      for (const lang of changes['languages'].currentValue) {
        const item = {
          isDisabled: false,
          text: lang.nativeName,
          value: lang.id
        } as SelectListItem;
        localLanguages.push(item);
      }
      this.minLanguageId = Math.min(...changes['languages'].currentValue.map((l: LanguageModel) => l.id));
      this.maxLanguageId = Math.max(...changes['languages'].currentValue.map((l: LanguageModel) => l.id));
      this.localLanguages$.next(localLanguages);
    }

    if (changes && changes['group']) {
      this.isAddPreviousEnabled = this._getIsAddPreviousEnabled();
      if (this.group) {
        this.respondent.groupId = this.group.id;
      }
    }

    if (changes && changes['respondentIndex'] && changes['respondentIndex'].currentValue) {
      const maximumRespondentIndex = this.response?.numberOfRespondentsPerGroup ?? Constants.NUMBER_OF_RESPONDENTS_PER_GROUP;

      if (this.respondentIndex < 1) this.respondentIndex = 1;
      if (this.respondentIndex > maximumRespondentIndex) this.respondentIndex = maximumRespondentIndex;

      if (this.group) {
        let clash = this.group.respondents.find((r: RespondentModel) => {
          r.groupIndex === this.respondentIndex && r.isSet;
        });
        if (clash) this.onDismissModalClicked('Respondent index clash');
      }

      this.respondent.groupIndex = this.respondentIndex;
    }

    if (changes && changes['respondent'] && changes['respondent'].currentValue) {
      this.respondentForm.get('firstName')?.setValue(this.respondent.firstName);
      this.respondentForm.get('lastName')?.setValue(this.respondent.lastName);
      this.respondentForm.get('emailAddress')?.setValue(this.respondent.emailAddress);
      this.respondentForm.get('language')?.setValue(this.respondent.languageId);
      this.respondentForm.get('personalMessage')?.setValue(this.respondent.personalMessage);
      this.respondentForm.markAsDirty();
    }

    if (changes && changes['isAddMode']) {
      if (!this.isAddMode) {
        this.respondentForm.get('firstName')?.disable();
        this.respondentForm.get('lastName')?.disable();
        this.respondentForm.get('emailAddress')?.disable();
        this.respondentForm.get('language')?.disable();
        this._renderer.selectRootElement('#personalMessage').focus();

      }
    }

    if (changes && changes['respondentValidationResult'] && changes['respondentValidationResult'].currentValue) {
      this.respondentForm.enable();
      this.respondent.isValidatingEmailAddress = false;
      this.respondent.isValid = !!this.respondentValidationResult?.isValid;
      if (this.respondentValidationResult?.isValid) {
        this.isValid = true;
        if (this.respondentValidationResult.firstName) {
          this.respondentForm.get('firstName')?.setValue(this.respondentValidationResult.firstName);
        }
        if (this.respondentValidationResult.lastName) {
          this.respondentForm.get('lastName')?.setValue(this.respondentValidationResult.lastName);
        }
        if (this.respondentValidationResult.languageId) {
          this.respondentForm.get('language')?.setValue(this.respondentValidationResult.languageId);
        }

        if (this.genericValidator) {
          this.displayMessage = this.genericValidator.processMessages(
            this.respondentForm
          )
        }
      } else {
        this.validationErrorMessage = this.respondentValidationResult?.errorMessage ?? this._translateService.instant('v2GeneralCommandErrorMessage');
        this.isValid = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.clearValidation.emit();
  }

  ngAfterViewInit(): void {
    if (!this.isAddMode) {
      this._renderer.selectRootElement('#personalMessage').focus();
    }
  }

  onBackButtonClicked(): void {
    this.backButtonClicked.emit();
  }

  onDismissModalClicked(reason: string | undefined = undefined) {
    this.dismissModal.emit(reason);
  }

  onSubmit(): void {
    this._saveRespondent();
  }

  onEmailAddressBlur(e: Event): void {
    if (!this.group) return;
    if (!this.respondentForm) return;
    if (!this.response) return;

    e.stopPropagation();
    if (
      this.respondentForm.get('emailAddress')?.valid &&
      this.respondentForm.get('emailAddress')?.dirty &&
      this.respondentForm.get('emailAddress')?.value.trim() !== this.respondent.emailAddress &&
      this.respondentForm.get('emailAddress')?.value.trim() !== this.currentEmailAddress
    ) {
      this.currentEmailAddress = this.respondentForm.get('emailAddress')?.value.trim();

      this.respondentForm.disable();
      this.respondentForm.get('firstName')?.setValue('');
      this.respondentForm.get('lastName')?.setValue('');
      this.respondentForm.get('language')?.setValue('');

      this.respondentForm.get('personalMessage')?.setValue('');
      this.respondent.isValidatingEmailAddress = true;
      this.isValid = false;
      this.validationErrorMessage = '';
      this.emailAddressChanged.emit({
        emailAddress: this.respondentForm.get('emailAddress')?.value,
        respondentGroupIndex: this.respondentIndex,
        groupId: this.group.id,
        participantId: this.response.id
      } as RespondentEmailValidationModel);
    }
  }

  onMessageChanged($event: Event) {
    if (!this.genericValidator) return;
    if (!this.respondentForm) return;

    this.displayMessage = this.genericValidator.processMessages(
      this.respondentForm
    );
  }

  onMessageBlur(change: FocusEvent) {
    this.onMessageChanged(change);
  }

  onLanguageBlur() {
    if (!this.genericValidator) return;
    if (!this.respondentForm) return;

    this.displayMessage = this.genericValidator.processMessages(
      this.respondentForm
    );
  }

  onLanguageValueChanged(event: any): void {
    if (!this.genericValidator) return;
    if (!this.respondentForm) return;

    this.displayMessage = this.genericValidator.processMessages(
      this.respondentForm
    );
  }

  onBlur() {
    if (!this.genericValidator) return;
    if (!this.respondentForm) return;

    this.displayMessage = this.genericValidator.processMessages(
      this.respondentForm
    );
  }

  private _getIsAddPreviousEnabled(): boolean {
    if (!this.group) {
      return false;
    }

    const maximumNumberOfRespondentsPerGroup: number = this.response?.numberOfRespondentsPerGroup ?? Constants.NUMBER_OF_RESPONDENTS_PER_GROUP;

    if (this.group.numberOfRespondents && this.group.numberOfRespondents >= maximumNumberOfRespondentsPerGroup) {
      return false;
    }

    return true;
     // "   !group || !group?.isSet || group?.numberOfRespondents >= maximumNumberOfRespondentsPerGroup"
  }

  private _saveRespondent() {
    if (!this.genericValidator) return;
    if (!this.respondentForm) return;

    if (!this.respondent.isValid) {
      return;
    }

    this.displayMessage = this.genericValidator.processMessages(
      this.respondentForm,
      true
    );

    if (!this.respondentForm.valid) {
      this.respondent.isSaving = false;
      return;
    }

    if (!this.respondentForm.dirty) {
      return;
    }

    const form = { ...this.respondentForm.value };
    this.respondent.isSaving = true;

    if (this.isAddMode) {
      this.respondent.emailAddress = form.emailAddress;
      this.respondent.firstName = form.firstName;
      this.respondent.lastName = form.lastName;
      this.respondent.languageId = form.language;

    }

    this.respondent.personalMessage = form.personalMessage;

    this.saveRespondent.emit(this.respondent);
  }

  onInvitePreviousClicked(arg0: GroupModel) {
    this.invitePreviousClicked.emit();
  }

}
